import { MetadataType } from '@aibees/elephant/hooks';
import { SearchType } from '@aibees/elephant/types';
import {
  FilterBadgeType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';

const SOCIAL_PLATFORMS = [
  {
    label: 'LinkedIn',
    value: 'linkedin',
    enabled: true,
    open: true,
  },
  {
    label: 'Facebook',
    value: 'facebook',
    enabled: false,
    open: false,
  },
  {
    label: 'X (Twitter)',
    value: 'x',
    enabled: false,
    open: false,
  },
  {
    label: 'Instagram',
    value: 'instagram',
    enabled: false,
    open: false,
  },
];

type Platform = (typeof SOCIAL_PLATFORMS)[number];
type Kind = 'followers' | 'connections';

const kindLabelOf = (kind: Kind) =>
  kind === 'followers' ? 'Followers' : 'Connections';

export default function socialMediaFollowersFilterConfig(
  type: SearchType
): SearchFilterBase {
  const isCompany = type === SearchType.Company;
  const scopePrefix = isCompany ? 'company' : 'person';

  const metadataKeyFor = (
    platform: Platform,
    kind: Kind
  ): MetadataType | undefined => {
    if (platform.value !== 'linkedin') return undefined;
    if (kind === 'followers') {
      return isCompany
        ? MetadataType.COMPANY_LINKEDIN_FOLLOWERS
        : MetadataType.PERSON_LINKEDIN_FOLLOWERS;
    }
    return MetadataType.PERSON_LINKEDIN_CONNECTIONS;
  };

  const buildRangeRadioGroup = (
    platform: Platform,
    kind: Kind
  ): SearchFilter => {
    const metaDataApiKey = metadataKeyFor(platform, kind);
    return {
      name: '',
      type: SearchFilterType.RADIO_GROUP,
      index: 0,
      key: `${scopePrefix}_${platform.value}_${kind}_range_type`,
      field: `${scopePrefix}_${platform.value}_${kind}_range_type`,
      sections: { main: 2, more: 1 },
      items: [
        {
          name: 'Predefined Range',
          type: SearchFilterType.CHECK_BOX,
          index: 0,
          layer: 4,
          key: `${scopePrefix}_${platform.value}_${kind}_predefined`,
          field: `${scopePrefix}_${platform.value}_${kind}_predefined`,
          metadata: {
            color: 'primary',
            ...(metaDataApiKey && {
              metaDataApi: { enabled: true, key: metaDataApiKey },
            }),
          },
          options: [],
          open: true,
          nested: true,
        },
        {
          name: 'Custom Range',
          type: SearchFilterType.NUMBER_RANGE,
          index: 1,
          layer: 4,
          key: `${scopePrefix}_${platform.value}_${kind}_custom`,
          field: `${scopePrefix}_${platform.value}_${kind}_custom`,
          metadata: { color: 'primary' },
          nested: true,
        },
        {
          name: `${platform.label} ${kindLabelOf(kind)} N/A`,
          type: SearchFilterType.NOT_AVAILABLE,
          icon: undefined,
          index: 2,
          layer: 4,
          key: `${scopePrefix}_${platform.value}_${kind}_availability`,
          field: `${scopePrefix}_${platform.value}_${kind}_availability`,
          metadata: { color: 'primary' },
          nested: true,
        },
      ],
    };
  };

  const buildKindTab = (
    platform: Platform,
    kind: Kind,
    index: number
  ): SearchFilter => ({
    name: kindLabelOf(kind),
    type: SearchFilterType.NONE,
    index,
    key: `${platform.value}_${kind}`,
    field: `${platform.value}_${kind}`,
    open: kind === 'followers',
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: `${platform.label} ${kindLabelOf(kind)}`,
        },
      },
    },
    items: [buildRangeRadioGroup(platform, kind)],
  });

  const buildPlatformTab = (
    platform: Platform,
    index: number,
    hasConnections: boolean
  ): SearchFilter => ({
    name: platform.label,
    type: SearchFilterType.NONE,
    index,
    key: platform.value,
    field: platform.value,
    comingSoon: !platform.enabled,
    disabled: !platform.enabled,
    open: platform.open,
    utils: {
      tags: {
        config: {
          // suppress the platform card when an inner kind BUTTON_GROUP renders
          // its own cards — otherwise the closed-state shows card-in-card
          showAsCard: !hasConnections,
          // platform card only wraps the followers radio group (connections
          // gets its own card via buildKindTab) — suffix is always "Followers"
          tagsCardTitle: `${platform.label} Followers`,
        },
      },
    },
    items: hasConnections
      ? [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            index: 0,
            key: `${platform.value}_kind_group`,
            field: `${platform.value}_kind_group`,
            config: { labelKey: 'label', valueKey: 'value' },
            metadata: { allowParallelItems: true },
            items: [
              {
                name: 'Applied Filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 2,
                icon: undefined,
                key: `${platform.value}_kind_applied_filters`,
                field: `${platform.value}_kind_applied_filters`,
                metadata: {},
              },
              buildKindTab(platform, 'followers', 0),
              buildKindTab(platform, 'connections', 1),
            ],
          },
        ]
      : [buildRangeRadioGroup(platform, 'followers')],
  });

  const appliedFilters: SearchFilter = {
    name: 'Applied Filters',
    type: SearchFilterType.APPLIED_FILTERS,
    index: 2,
    icon: undefined,
    key: 'social_media_followers_applied_filters',
    field: 'social_media_followers_applied_filters',
    metadata: {},
  };

  return {
    name: 'Social Media Followers',
    icon: 'subscriptions',
    index: 23,
    type: SearchFilterType.ROOT,
    category: isCompany
      ? [FilterCategory.COMPANY_DATA]
      : [FilterCategory.CONTACT_DATA],
    pinned: !isCompany,
    disabled: isCompany,
    comingSoon: isCompany,
    badge: !isCompany ? FilterBadgeType.NEW : undefined,
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        index: 0,
        key: 'social_media_followers',
        field: 'social_media_followers',
        config: { labelKey: 'label', valueKey: 'value' },
        metadata: { allowParallelItems: true },
        items: [
          appliedFilters,
          ...SOCIAL_PLATFORMS.map((platform, idx) =>
            buildPlatformTab(
              platform,
              idx,
              !isCompany && platform.value === 'linkedin'
            )
          ),
        ],
      },
    ],
  };
}
